import { useEffect, useState } from 'react';
import {
  BasicTableDataRow,
  FormTemplate,
  FormVersion,
  FormTypeEnum,
  TableCellContentAlignEnum,
  TableHeaderItem,
  TableSortOption,
} from '@employ-engage/data';
import { OptionsIndicator } from '../options-indicator/options-indicator';
import AddTemplateModal from '../add-template-modal/add-template-modal';
import cloneDeep from 'lodash.clonedeep';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import Table from '../table/table';
import Tooltip from '../tooltip/tooltip';
import DeleteTemplateConfirmationModal from '../delete-template-confirmation-modal/delete-template-confirmation-modal';
import DuplicateTemplateModal from '../duplicate-template-modal/duplicate-template-modal';
import TemplateMarketplaceModal from '../template-marketplace-modal/template-marketplace-modal';
import { FormTypePickerModal } from '../form-type-picker-modal/form-type-picker-modal';
import { useNavigate } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import AddTemplateFromAIModal from '../add-template-from-ai-modal/add-template-from-ai-modal';
import { Button } from '../button/button';
import VersionsModal from '../versions-modal/versions-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useAuthUser } from '../contexts/auth-context-type';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_ALL_FORM_TEMPLATES_FOR_CURRENT_USER,
  CREATE_FORM_VERSION,
} from '../api';
import { useCurrentCompany } from '../contexts';
import { Trans } from 'react-i18next';

export interface AdminFormTemplatesViewProps {
  shouldShowTemplateMarketplace?: boolean;
  isPublishingEnabled?: boolean;
  isGlobal?: boolean;
}

export function AdminFormTemplatesView({
  shouldShowTemplateMarketplace,
  isPublishingEnabled,
  isGlobal,
}: AdminFormTemplatesViewProps) {
  const { t } = useTranslation(['common']);
  const flags = useFlags();
  const [isAddTemplateModalOpen, setIsAddTemplateModalOpen] = useState(false);
  const [isAddTemplateFromAIModalOpen, setIsAddTemplateFromAIModalOpen] =
    useState(false);
  const [templateSelectedForEditing, setTemplateSelectedForEditing] =
    useState<FormTemplate | null>(null);
  const [templateSelectedForDeletion, setTemplateSelectedForDeletion] =
    useState<FormTemplate>();
  const [templateSelectedForVersions, setTemplateSelectedForVersions] =
    useState<FormTemplate | null>(null);
  const [
    isDeleteTemplateConfirmationModalOpen,
    setIsDeleteTemplateConfirmationModalOpen,
  ] = useState(false);
  const [publishToggle, setPublishToggle] = useState(false);
  const [templateSelectedForDuplication, setTemplateSelectedForDuplication] =
    useState<FormTemplate>();
  const [isDuplicateTemplateModalOpen, setIsDuplicateTemplateModalOpen] =
    useState(false);
  const [openTemplateMarketplaceModal, setOpenTemplateMarketplaceModal] =
    useState(false);
  const [openFormTypePickerModal, setOpenFormTypePickerModal] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuthUser();
  const [openVersionsModal, setOpenVersionsModal] = useState<boolean>(false);
  const { currentCompany } = useCurrentCompany();

  const sortOptions: TableSortOption[] = [
    {
      value: 'Title (A-Z)',
      sort: (row1, row2) => {
        return row1.name.localeCompare(row2.name);
      },
    },
    {
      value: 'Title (Z-A)',
      sort: (row1, row2) => {
        return row2.name.localeCompare(row1.name);
      },
    },
    {
      value: 'Created Date (Oldest)',
      sort: (row1, row2) => {
        return row1.createdAt.seconds - row2.createdAt.seconds;
      },
    },
    {
      value: 'Created Date (Newest)',
      sort: (row1, row2) => {
        return row2.createdAt.seconds - row1.createdAt.seconds;
      },
    },
    {
      value: 'Updated Date (Newest)',
      sort: (row1, row2) => {
        return row1.updatedAt.seconds - row2.updatedAt.seconds;
      },
    },
    {
      value: 'Updated Date (Oldest)',
      sort: (row1, row2) => {
        return row2.updatedAt.seconds - row1.updatedAt.seconds;
      },
    },
  ];
  const [activeSort, setActiveSort] = useState<TableSortOption>(sortOptions[0]);

  const {
    data: { getAllFormTemplatesForCurrentUser: formTemplates } = {},
    loading: isFormTemplatesLoading,
    refetch: refetchFormTemplates,
  } = useQuery(GET_ALL_FORM_TEMPLATES_FOR_CURRENT_USER);

  useEffect(() => {
    if (user?.currentCompanyId) {
      refetchFormTemplates({
        companyId: user.currentCompanyId,
      });
    }
  }, [user?.currentCompanyId]);

  const [createTemplateVersion] = useMutation(CREATE_FORM_VERSION);

  const publishNewTemplateVersion = async (template: FormTemplate) => {
    if (isGlobal) return null;

    const templateVersion = {
      name: `${template.name} - v${(template?.versionIds?.length || 0) + 1}`,
      pages: cloneDeep(template.pages),
      templateId: template.id,
      completionButtonLabel: template.completionButtonLabel,
      isAnonymous: template.isAnonymous,
      isInfinitelyRespondable: template.isInfinitelyRespondable,
      isPasscodeRequired: template.isPasscodeRequired,
      ownerId: user?.id,
    } as unknown as FormVersion;

    if (template.type === FormTypeEnum.FROM_SCRATCH) {
      templateVersion.welcomePageRichText = template.welcomePageRichText;
    }

    await createTemplateVersion({
      variables: {
        input: { ...templateVersion, companyId: user?.currentCompanyId },
      },
    });

    setPublishToggle(!publishToggle);
    refetchFormTemplates();

    return Promise.resolve(true);
  };

  useEffect(() => {
    if (templateSelectedForDeletion) {
      setIsDeleteTemplateConfirmationModalOpen(true);
    } else {
      setIsDeleteTemplateConfirmationModalOpen(false);
    }
  }, [templateSelectedForDeletion]);

  if (isFormTemplatesLoading) {
    return (
      <LoadingSpinner spinnerId="admin-form-templates-loading" makeAbsolute />
    );
  }

  const dataList = [...(formTemplates ? formTemplates : [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const headerList: TableHeaderItem[] = [
    {
      id: 'name',
      headerLabel: t('Name'),
    },
  ];

  if (isGlobal || flags['ee-main-image-form-builder']) {
    headerList.push({
      id: 'type',
      headerLabel: t('Type'),
      alignment: TableCellContentAlignEnum.CENTER,
    });
  }

  headerList.push(
    {
      id: 'createdAt',
      headerLabel: t('Created Date'),
      alignment: TableCellContentAlignEnum.CENTER,
    },
    {
      id: 'updatedAt',
      headerLabel: t('Updated Date'),
      alignment: TableCellContentAlignEnum.CENTER,
    }
  );

  if (isPublishingEnabled) {
    headerList.push(
      {
        id: 'numberOfVersions',
        headerLabel: t('Number of Versions'),
        alignment: TableCellContentAlignEnum.CENTER,
      },
      {
        id: 'changesPublished',
        headerLabel: t('Publish Status'),
        alignment: TableCellContentAlignEnum.CENTER,
      }
    );
  }

  headerList.push(
    {
      id: 'options',
      headerLabel: t('Options'),
      alignment: TableCellContentAlignEnum.CENTER,
    },
    {
      id: 'actions',
      headerLabel: t('Actions'),
    }
  );

  return (
    <div className="space-y-7 flex flex-col">
      <div className="w-full pl-9 pr-[35.92px] pt-[37.91px] pb-[36.99px] bg-gradient-to-l from-light-primary to-light-primary rounded-lg justify-center items-center inline-flex">
        <div className="grow shrink basis-0 self-stretch justify-between items-center inline-flex">
          <div className="flex-col justify-start gap-[8.10px] inline-flex">
            <div className="text-white text-2xl font-bold">
              {t('New Template')}
            </div>
            <div className="text-gray-200 text-base font-normal">
              <Trans i18nKey="adminFormTemplatesViewNewFormDescription">
                Create a new template to collect information from your
                employees.
                <span className="font-bold">
                  NOTE: Templates are required before creating forms. Forms can
                  only be created from templates that are published.
                </span>
                Every time you publish changes to a template, a new version is
                created. And any time you change a template that is already in
                use by a form, you can choose to publish those changes as a new
                version without affecting existing forms using previous
                versions.
              </Trans>
            </div>
          </div>
        </div>
      </div>
      <Table
        sortOptions={sortOptions.map((option) => ({
          ...option,
          value: t(option.value),
        }))}
        activeSort={{ ...activeSort, value: t(activeSort.value) }}
        setActiveSort={setActiveSort}
        label={t('Templates')}
        headerList={headerList}
        actionItems={
          <>
            {shouldShowTemplateMarketplace && (
              <Tooltip label={t('Template Marketplace')}>
                <button
                  onClick={() => {
                    return setOpenTemplateMarketplaceModal(true);
                  }}
                  className="w-10 h-10 text-center align-middle rounded-xl bg-light-primary hover:bg-light-primaryAccent dark:bg-dark-primary hover:dark:bg-dark-primaryAccent mr-2"
                >
                  <i className="fas fa-bag-shopping text-light-primaryBackground dark:text-custom-textColor text-lg" />
                </button>
              </Tooltip>
            )}
            <Tooltip label={t('Add Template')}>
              <button
                onClick={() => setOpenFormTypePickerModal(true)}
                className="w-10 h-10 text-center align-middle rounded-xl bg-light-primary hover:bg-light-primaryAccent dark:bg-dark-primary hover:dark:bg-dark-primaryAccent"
              >
                <i className="fa-solid fa-plus text-light-primaryBackground dark:text-custom-textColor text-lg" />
              </button>
            </Tooltip>
          </>
        }
        dataList={dataList as unknown as BasicTableDataRow[]}
        mapData={(row, index) => {
          const options = [
            {
              activeColor: 'text-orange-500',
              isActive: row.isAnonymous || false,
              icon: 'fa-question',
              label: t('Anonymous'),
            },
            {
              activeColor: 'text-purple-500',
              isActive: row.isInfinitelyRespondable || false,
              icon: 'fa-infinity',
              label: t('Infinite Responses'),
            },
            {
              activeColor: 'text-blue-500',
              isActive: row.isPasscodeRequired || false,
              icon: 'fa-key',
              label: t('Passcode Required'),
            },

          ];

          return {
            index,
            id: row.id,
            name: row.name,
            type: (
              <Tooltip label={t('From Scratch')}>
                <i className="fa-solid fa-screwdriver-wrench text-green-700" />
              </Tooltip>
            ),
            changesPublished: row.changesPublished ? (
              <span className="py-1 px-2 bg-green-400 rounded-full text-white">
                {t('published')}
              </span>
            ) : (
              <span className="py-1 px-2 bg-gray-400 rounded-full text-white">
                {t('draft')}
              </span>
            ),
            numberOfVersions: (
              <Button
                variant="link"
                onClick={() => {
                  setTemplateSelectedForVersions(
                    row as unknown as FormTemplate
                  );
                  setOpenVersionsModal(true);
                }}
              >
                {row.versionIds.length}
              </Button>
            ),
            createdAt: row.createdAt
              ? format(row.createdAt, 'MMM dd, yyyy')
              : '',
            updatedAt: row.updatedAt
              ? format(row.updatedAt, 'MMM dd, yyyy')
              : '',
            options: <OptionsIndicator options={options} />,
            actions: (
              <div className="flex space-x-2">
                {isPublishingEnabled && (
                  <Tooltip
                    label={t('Publish New Version', {
                      addon: row.changesPublished
                        ? t('(already published)')
                        : '',
                    })}
                  >
                    <button
                      disabled={!!row.changesPublished}
                      onClick={() => publishNewTemplateVersion(row as any)}
                    >
                      <FontAwesomeIcon
                        icon="upload"
                        className={
                          row.changesPublished
                            ? 'text-gray-400 dark:text-gray-400 cursor-not-allowed'
                            : 'text-light-primaryText dark:text-dark-primaryText hover:text-blue-500 hover:dark:text-blue-500'
                        }
                      />
                    </button>
                  </Tooltip>
                )}
                <Tooltip label={t('Edit')}>
                  <button
                    onClick={() => {
                      setTemplateSelectedForEditing(
                        row as unknown as FormTemplate
                      );
                      setIsAddTemplateModalOpen(true);
                    }}
                  >
                    <i className="fa-solid fa-pen-to-square text-light-primaryText dark:text-dark-primaryText hover:text-blue-500 hover:dark:text-blue-500" />
                  </button>
                </Tooltip>
                <Tooltip label={t('Duplicate Template')}>
                  <button
                    onClick={() => {
                      setTemplateSelectedForDuplication(
                        row as unknown as FormTemplate
                      );
                      setIsDuplicateTemplateModalOpen(true);
                    }}
                  >
                    <i className="fa-solid fa-copy text-light-primaryText dark:text-dark-primaryText hover:text-blue-500 hover:dark:text-blue-500" />
                  </button>
                </Tooltip>
                <Tooltip label={t('Delete Template')}>
                  <button
                    onClick={() => {
                      setTemplateSelectedForDeletion(
                        row as unknown as FormTemplate
                      );
                    }}
                  >
                    <i className="fa-solid fa-trash text-light-primaryText dark:text-dark-primaryText hover:text-blue-500 hover:dark:text-blue-500" />
                  </button>
                </Tooltip>
              </div>
            ),
          };
        }}
      />
      <VersionsModal
        open={openVersionsModal}
        setOpen={setOpenVersionsModal}
        selectedTemplate={templateSelectedForVersions}
      />
      <AddTemplateModal
        open={isAddTemplateModalOpen}
        setOpen={setIsAddTemplateModalOpen}
        templateSelectedForEditing={templateSelectedForEditing}
        refetchFormTemplates={refetchFormTemplates}
      />
      <AddTemplateFromAIModal
        open={isAddTemplateFromAIModalOpen}
        setOpen={setIsAddTemplateFromAIModalOpen}
        onSave={(surveyTemplateId) => {
          setIsAddTemplateFromAIModalOpen(false);
          refetchFormTemplates();
        }}
      />
      <DeleteTemplateConfirmationModal
        open={isDeleteTemplateConfirmationModalOpen}
        setOpen={setIsDeleteTemplateConfirmationModalOpen}
        templateSelectedForDeletion={templateSelectedForDeletion}
        refetchFormTemplates={refetchFormTemplates}
      />
      <DuplicateTemplateModal
        open={isDuplicateTemplateModalOpen}
        setOpen={setIsDuplicateTemplateModalOpen}
        templateSelectedForDuplication={templateSelectedForDuplication}
        refetchFormTemplates={refetchFormTemplates}
      />
      <TemplateMarketplaceModal
        open={openTemplateMarketplaceModal}
        setOpen={setOpenTemplateMarketplaceModal}
      />
      <FormTypePickerModal
        isOpen={openFormTypePickerModal}
        setOpen={setOpenFormTypePickerModal}
        onFormTypeSelected={(formTypeSelected: FormTypeEnum) => {
          setTemplateSelectedForEditing(null);
          setOpenFormTypePickerModal(false);

          if (formTypeSelected === FormTypeEnum.FROM_SCRATCH) {
            setIsAddTemplateModalOpen(true);
          } else if (formTypeSelected === FormTypeEnum.FROM_AI) {
            setIsAddTemplateFromAIModalOpen(true);
          } else {
            navigate(
              `/${currentCompany.friendlyCompanyId}/admin/templates/builder`
            );
          }
        }}
      />
    </div>
  );
}

export default AdminFormTemplatesView;
